import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Row } from 'react-table';
import { useTranslation } from 'react-i18next';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { RULE_TEST_STATUS } from 'Common/enums';
import Table from 'Components/Table';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters/useGetRuleQuickFilters';
import useGetManageRuleTestsColumns from '../../hooks/useGetManageRuleTestsColumns';
import useGetManageRuleTestsData from '../../hooks/useGetManageRuleTestsData';

import styles from './ManageRuleTestsTable.css';

const TABLE_ID = 'ManageRuleTestsTable';

type Props = {
    showKebabMenuCol: boolean;
    hideCompareCol: boolean;
};

const ManageRuleTestsTable = ({ showKebabMenuCol, hideCompareCol }: Props) => {
    const { t } = useTranslation();
    const rawColumns = useGetManageRuleTestsColumns(showKebabMenuCol, hideCompareCol);
    const rawData: any[] = useGetManageRuleTestsData();
    const history = useHistory();
    const { path } = useRouteMatch();
    const { addTechnicalErrorModal } = useReduxModal();

    const quickFilters = useGetRuleQuickFilters({
        quickFilters: ['newRuleTests', 'ruleTestsInProgress'],
        qfOverrides: [
            {
                id: 'ruleTestsInProgress',
                data: [
                    {
                        id: 'ruleTestsInProgress',
                        text: t('In Progress'),
                        options: {
                            conditional: 'arrayIncludes',
                            valueToMatch: [RULE_TEST_STATUS.QUEUED, RULE_TEST_STATUS.IN_PROGRESS],
                            fieldToMatch: 'status',
                            iconName: 'in-progress'
                        }
                    }
                ]
            },
            {
                id: 'newRuleTests',
                data: [
                    {
                        id: 'newRuleTests',
                        text: t('New'),
                        options: {
                            conditional: 'eq',
                            valueToMatch: true,
                            fieldToMatch: '_formatted.isNewRuleTest',
                            iconName: 'table-new'
                        }
                    }
                ]
            }
        ]
    });

    const handleRowClick = async (
        row: Row<RuleTest>,
        e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
    ) => {
        const targetAsAny = e.target as any; // do this so that we can do things like contains
        if (
            e.currentTarget.contains(targetAsAny) &&
            // hack to see if the target is something we can interact with
            !targetAsAny?.getAttribute?.('data-ignore-on-row-interact') &&
            row.original.status === RULE_TEST_STATUS.FINISHED
        ) {
            try {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Manage Rule Tests - View Rule Test',
                        text: 'View Rule Test'
                    }
                });
                // TODO: check if rule test exists
                history.push(`${path}/${row?.original.id}`);
                // TODO: invalidate all rule_tests
            } catch (error) {
                const content = t('alreadyDeletedAccessedRuleTest');
                addTechnicalErrorModal(
                    '',
                    t('Rule test is already deleted'),
                    () => {
                        // TODO: invalidate all rule_tests
                        history.push('/rule-tests');
                    },
                    {
                        status: 'warning',
                        content
                    }
                );
            }
        }
    };

    return (
        <div className={styles.ruleTestsTable}>
            <Table
                tableName={TABLE_ID}
                dataTestId={TABLE_ID}
                showExpandableSearch
                showRichFilter
                showMultiSort
                clickableRow
                enablePagination
                initialState={{
                    pageSize: 20,
                    sortBy: [{ id: '_formatted.createdOn', desc: true }],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                noDataMessage='noRuleTestsData'
                columns={rawColumns}
                data={rawData}
                quickFilters={quickFilters.filters}
                onRowInteractCB={(row, e) => handleRowClick(row, e)}
            />
        </div>
    );
};

export default React.memo(ManageRuleTestsTable);
